import { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
    Alert,
    Tabs,
    Tab,
    TextField,
    Stack,
    Divider,
    LinearProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import {
    Visibility as VisibilityIcon,
    CheckCircle as ApproveIcon,
    Cancel as RejectIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Save as SaveIcon,
    Close as CloseIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import { getClientPrincipal } from '../services/auth';
import {
    fetchAllEventForms,
    approveEventForm,
    rejectEventForm,
    deleteEventForm,
    updateEventCost,
    updateEventFinancialYear,
    parseParticipants,
    parseCosts,
    EventFormClient
} from '../services/eventForms';
import {
    sendEventApproval,
    sendEventRejection
} from '../services/email';
import {
    formatFYLabel,
    getFormFinancialYear,
    getCurrentFinancialYear,
    formatGBP,
    buildFinancialYearOptions
} from '../utils/financialYear';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            {...other}
        >
            {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
        </div>
    );
}

// Annual BD Events / Conferences budget (May–April financial year)
const BD_EVENTS_BUDGET = 18000;

// Fraction of budget at which the progress bar turns amber
const BUDGET_WARNING_THRESHOLD_PERCENT = 90;

const EventsAdminPage = () => {
    const [eventForms, setEventForms] = useState<EventFormClient[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedForm, setSelectedForm] = useState<EventFormClient | null>(null);
    const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [tabValue, setTabValue] = useState(0);
    const [processing, setProcessing] = useState(false);
    const [userEmail, setUserEmail] = useState<string | null>(null);
    const [accessDenied, setAccessDenied] = useState(false);
    const [selectedFY, setSelectedFY] = useState<number>(getCurrentFinancialYear());
    const [editingCostId, setEditingCostId] = useState<number | null>(null);
    const [editingCostValue, setEditingCostValue] = useState<string>('');
    const [editingFinancialYearId, setEditingFinancialYearId] = useState<number | null>(null);
    const [editingFinancialYearValue, setEditingFinancialYearValue] = useState<number>(getCurrentFinancialYear());

    const EVENTS_ADMIN_EMAILS = [
        'si@iodparc.com',
        'joanne@iodparc.com',
        'pam@iodparc.com',
        'naomi@iodparc.com',
        'ima@iodparc.com',
        'roeland@iodparc.com',
        'sadie@iodparc.com',
        'erica@iodparc.com',
        'enrique@iodparc.com',
        'karolina@iodparc.com',
        'sarah@iodparc.com',
        'hatty@iodparc.com',
        'victoria@iodparc.com'
    ];

    useEffect(() => {
        checkAccess();
    }, []);

    const checkAccess = async () => {
        try {
            const principal = await getClientPrincipal();
            const email = principal?.userDetails?.toLowerCase() || null;
            setUserEmail(email);

            if (!email || !EVENTS_ADMIN_EMAILS.includes(email)) {
                setAccessDenied(true);
                setLoading(false);
                return;
            }

            loadEventForms();
        } catch (err) {
            console.error('Error checking access:', err);
            setAccessDenied(true);
            setLoading(false);
        }
    };

    const loadEventForms = async () => {
        try {
            setLoading(true);
            const forms = await fetchAllEventForms();
            setEventForms(forms);
        } catch (err) {
            console.error('Error loading event forms:', err);
            setError('Failed to load event forms');
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetails = (form: EventFormClient) => {
        setSelectedForm(form);
        setDetailsDialogOpen(true);
    };

    const handleApprove = async (form: EventFormClient) => {
        try {
            setProcessing(true);
            const principal = await getClientPrincipal();
            if (!principal?.name) {
                throw new Error('User not authenticated');
            }

            await approveEventForm(form.id!, principal.name);

            await sendEventApproval({
                submitter_email: form.submitter_email,
                submitter_name: form.submitter_name,
                event_title: form.event_title,
                dates: form.dates,
                location: form.location,
                approved_by: principal.name
            });

            await loadEventForms();
            setDetailsDialogOpen(false);
        } catch (err) {
            console.error('Error approving event form:', err);
            alert('Failed to approve event form');
        } finally {
            setProcessing(false);
        }
    };

    const handleRejectClick = (form: EventFormClient) => {
        setSelectedForm(form);
        setRejectionReason('');
        setRejectDialogOpen(true);
    };

    const handleRejectConfirm = async () => {
        if (!selectedForm || !rejectionReason.trim()) {
            alert('Please provide a rejection reason');
            return;
        }

        try {
            setProcessing(true);
            await rejectEventForm(selectedForm.id!, rejectionReason);

            await sendEventRejection({
                submitter_email: selectedForm.submitter_email,
                submitter_name: selectedForm.submitter_name,
                event_title: selectedForm.event_title,
                dates: selectedForm.dates,
                location: selectedForm.location,
                rejection_reason: rejectionReason
            });

            await loadEventForms();
            setRejectDialogOpen(false);
            setDetailsDialogOpen(false);
        } catch (err) {
            console.error('Error rejecting event form:', err);
            alert('Failed to reject event form');
        } finally {
            setProcessing(false);
        }
    };

    const handleDeleteClick = (form: EventFormClient) => {
        setSelectedForm(form);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedForm) return;

        try {
            setProcessing(true);
            await deleteEventForm(selectedForm.id!);

            await loadEventForms();
            setDeleteDialogOpen(false);
            setDetailsDialogOpen(false);
        } catch (err) {
            console.error('Error deleting event form:', err);
            alert('Failed to delete event form');
        } finally {
            setProcessing(false);
        }
    };

    const handleStartEditCost = (form: EventFormClient) => {
        setEditingCostId(form.id!);
        setEditingCostValue(form.total_cost != null ? String(form.total_cost) : '');
    };

    const handleCancelEditCost = () => {
        setEditingCostId(null);
        setEditingCostValue('');
    };

    const handleSaveCost = async (form: EventFormClient) => {
        const parsed = parseFloat(editingCostValue);
        if (isNaN(parsed) || parsed < 0) {
            alert('Please enter a valid cost');
            return;
        }

        try {
            setProcessing(true);
            await updateEventCost(form.id!, parsed);
            await loadEventForms();
            setEditingCostId(null);
            setEditingCostValue('');
        } catch (err) {
            console.error('Error updating event cost:', err);
            alert('Failed to update cost');
        } finally {
            setProcessing(false);
        }
    };

    const handleStartEditFinancialYear = (form: EventFormClient) => {
        setEditingFinancialYearId(form.id!);
        setEditingFinancialYearValue(getFormFinancialYear(form) ?? getCurrentFinancialYear());
    };

    const handleCancelEditFinancialYear = () => {
        setEditingFinancialYearId(null);
    };

    const handleSaveFinancialYear = async (form: EventFormClient) => {
        try {
            setProcessing(true);
            await updateEventFinancialYear(form.id!, editingFinancialYearValue);
            await loadEventForms();
            setSelectedFY(editingFinancialYearValue);
            setEditingFinancialYearId(null);
        } catch (err) {
            console.error('Error updating event financial year:', err);
            alert('Failed to update financial year');
        } finally {
            setProcessing(false);
        }
    };

    const getStatusColor = (status: string): "default" | "warning" | "success" | "error" => {
        switch (status) {
            case 'pending':
                return 'warning';
            case 'approved':
                return 'success';
            case 'rejected':
                return 'error';
            default:
                return 'default';
        }
    };

    const formatDate = (dateStr?: string) => {
        if (!dateStr) return 'N/A';
        try {
            return new Date(dateStr).toLocaleDateString();
        } catch {
            return dateStr;
        }
    };

    // Build the list of financial years present in the data, always including the current FY
    const availableFYs = Array.from(
        new Set<number>([
            getCurrentFinancialYear(),
            ...eventForms
                .map(getFormFinancialYear)
                .filter((fy): fy is number => fy != null)
        ])
    ).sort((a, b) => b - a);

    const financialYearOptions = buildFinancialYearOptions(
        eventForms.map(getFormFinancialYear)
    );

    const formsInSelectedFY = eventForms.filter(
        ef => getFormFinancialYear(ef) === selectedFY
    );

    const pendingForms = formsInSelectedFY.filter(ef => ef.status === 'pending');
    const pastForms = formsInSelectedFY.filter(ef => ef.status !== 'pending');

    // Approved spend for the selected financial year (approved events only)
    const approvedTotal = formsInSelectedFY
        .filter(ef => ef.status === 'approved')
        .reduce((sum, ef) => sum + (ef.total_cost || 0), 0);

    const budgetPercent = Math.min((approvedTotal / BD_EVENTS_BUDGET) * 100, 100);
    const remaining = BD_EVENTS_BUDGET - approvedTotal;
    const budgetColor: 'success' | 'warning' | 'error' =
        approvedTotal > BD_EVENTS_BUDGET
            ? 'error'
            : budgetPercent >= BUDGET_WARNING_THRESHOLD_PERCENT
                ? 'warning'
                : 'success';

    const renderCostCell = (form: EventFormClient) => {
        if (editingCostId === form.id) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TextField
                        value={editingCostValue}
                        onChange={(e) => setEditingCostValue(e.target.value)}
                        size="small"
                        type="number"
                        inputProps={{ min: 0, step: '0.01', style: { padding: '4px 6px' } }}
                        sx={{ width: 90 }}
                        autoFocus
                    />
                    <IconButton
                        onClick={() => handleSaveCost(form)}
                        size="small"
                        title="Save cost"
                        color="success"
                        disabled={processing}
                    >
                        <SaveIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        onClick={handleCancelEditCost}
                        size="small"
                        title="Cancel"
                        disabled={processing}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            );
        }

        const hasCost = form.total_cost != null && form.total_cost > 0;
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {hasCost ? (
                    <Typography variant="body2" noWrap>
                        {formatGBP(form.total_cost!)}
                    </Typography>
                ) : (
                    <Typography variant="caption" color="text.secondary" noWrap>
                        Add cost
                    </Typography>
                )}
                <IconButton
                    onClick={() => handleStartEditCost(form)}
                    size="small"
                    title="Edit cost"
                    disabled={processing}
                >
                    <EditIcon fontSize="small" />
                </IconButton>
            </Box>
        );
    };

    const renderFinancialYearCell = (form: EventFormClient) => {
        if (editingFinancialYearId === form.id) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Select
                        value={editingFinancialYearValue}
                        onChange={(event) => setEditingFinancialYearValue(Number(event.target.value))}
                        size="small"
                        aria-label={`Financial year for ${form.event_title}`}
                        sx={{ minWidth: 154 }}
                    >
                        {financialYearOptions.map((fy) => (
                            <MenuItem key={fy} value={fy}>
                                {formatFYLabel(fy)}
                            </MenuItem>
                        ))}
                    </Select>
                    <IconButton
                        onClick={() => handleSaveFinancialYear(form)}
                        size="small"
                        title="Save financial year"
                        color="success"
                        disabled={processing}
                    >
                        <SaveIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        onClick={handleCancelEditFinancialYear}
                        size="small"
                        title="Cancel"
                        disabled={processing}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            );
        }

        const financialYear = getFormFinancialYear(form);
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" noWrap>
                    {financialYear == null ? 'Not assigned' : formatFYLabel(financialYear)}
                </Typography>
                <IconButton
                    onClick={() => handleStartEditFinancialYear(form)}
                    size="small"
                    title="Reassign financial year"
                    disabled={processing}
                >
                    <EditIcon fontSize="small" />
                </IconButton>
            </Box>
        );
    };

    if (accessDenied) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Paper elevation={3} sx={{ p: 3 }}>
                    <Alert severity="error" sx={{ mb: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Access Denied
                        </Typography>
                        <Typography>
                            You do not have permission to access the Events Admin page.
                            This page is restricted to authorized personnel only.
                        </Typography>
                        {userEmail && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                Current user: {userEmail}
                            </Typography>
                        )}
                    </Alert>
                </Paper>
            </Container>
        );
    }

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert severity="error">{error}</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Paper elevation={3} sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="h4" sx={{ color: '#009688' }}>
                        Events Admin
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel id="events-fy-label">Financial Year</InputLabel>
                            <Select
                                labelId="events-fy-label"
                                label="Financial Year"
                                value={selectedFY}
                                onChange={(e) => setSelectedFY(Number(e.target.value))}
                            >
                                {availableFYs.map((fy) => (
                                    <MenuItem key={fy} value={fy}>
                                        {formatFYLabel(fy)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={loadEventForms}
                        >
                            Refresh
                        </Button>
                    </Box>
                </Box>

                {/* Budget summary for the selected financial year */}
                <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                            BD Events / Conferences Budget {formatFYLabel(selectedFY)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {formatGBP(approvedTotal)} approved of {formatGBP(BD_EVENTS_BUDGET)}
                            {remaining >= 0
                                ? ` · ${formatGBP(remaining)} remaining`
                                : ` · ${formatGBP(Math.abs(remaining))} over budget`}
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={budgetPercent}
                        color={budgetColor}
                        sx={{ height: 10, borderRadius: 5 }}
                    />
                </Box>

                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
                        <Tab label={`Pending (${pendingForms.length})`} />
                        <Tab label={`Past Events (${pastForms.length})`} />
                    </Tabs>
                </Box>

                <TabPanel value={tabValue} index={0}>
                    {pendingForms.length === 0 ? (
                        <Alert severity="info">No pending event requests</Alert>
                    ) : (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Event Title</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Dates</TableCell>
                                        <TableCell>Location</TableCell>
                                        <TableCell>Submitted By</TableCell>
                                        <TableCell>Submitted</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Cost</TableCell>
                                        <TableCell>Financial Year</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {pendingForms.map((form) => (
                                        <TableRow key={form.id}>
                                            <TableCell>{form.event_title}</TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{form.dates}</TableCell>
                                            <TableCell>{form.location}</TableCell>
                                            <TableCell>{form.submitter_name}</TableCell>
                                            <TableCell>{formatDate(form.submission_date)}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={form.status.toUpperCase()}
                                                    color={getStatusColor(form.status)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>{renderCostCell(form)}</TableCell>
                                            <TableCell>{renderFinancialYearCell(form)}</TableCell>
                                            <TableCell align="right">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleViewDetails(form)}
                                                    title="View Details"
                                                >
                                                    <VisibilityIcon />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    color="success"
                                                    onClick={() => handleApprove(form)}
                                                    title="Approve"
                                                    disabled={processing}
                                                >
                                                    <ApproveIcon />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleRejectClick(form)}
                                                    title="Reject"
                                                    disabled={processing}
                                                >
                                                    <RejectIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </TabPanel>

                <TabPanel value={tabValue} index={1}>
                    {pastForms.length === 0 ? (
                        <Alert severity="info">No past event requests</Alert>
                    ) : (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Event Title</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Dates</TableCell>
                                        <TableCell>Location</TableCell>
                                        <TableCell>Submitted By</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Cost</TableCell>
                                        <TableCell>Financial Year</TableCell>
                                        <TableCell>Decision By</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {pastForms.map((form) => (
                                        <TableRow key={form.id}>
                                            <TableCell>{form.event_title}</TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{form.dates}</TableCell>
                                            <TableCell>{form.location}</TableCell>
                                            <TableCell>{form.submitter_name}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={form.status.toUpperCase()}
                                                    color={getStatusColor(form.status)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>{renderCostCell(form)}</TableCell>
                                            <TableCell>{renderFinancialYearCell(form)}</TableCell>
                                            <TableCell>{form.approved_by || 'N/A'}</TableCell>
                                            <TableCell align="right">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleViewDetails(form)}
                                                    title="View Details"
                                                >
                                                    <VisibilityIcon />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDeleteClick(form)}
                                                    title="Delete"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </TabPanel>
            </Paper>

            {/* Details Dialog */}
            <Dialog
                open={detailsDialogOpen}
                onClose={() => setDetailsDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Event Request Details</DialogTitle>
                <DialogContent>
                    {selectedForm && (
                        <Box sx={{ pt: 2 }}>
                            <Typography variant="h6" gutterBottom>{selectedForm.event_title}</Typography>

                            <Stack spacing={2}>
                                <Divider />
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#009688' }}>
                                    Section 1: Event Details
                                </Typography>

                                <Box>
                                    <Typography variant="body2" color="text.secondary">Submitted By</Typography>
                                    <Typography variant="body1">{selectedForm.submitter_name} ({selectedForm.submitter_email})</Typography>
                                </Box>

                                <Box>
                                    <Typography variant="body2" color="text.secondary">Dates</Typography>
                                    <Typography variant="body1">{selectedForm.dates}</Typography>
                                </Box>

                                <Box>
                                    <Typography variant="body2" color="text.secondary">Location</Typography>
                                    <Typography variant="body1">{selectedForm.location}</Typography>
                                </Box>

                                {selectedForm.event_organisers && (
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Event Organiser(s)</Typography>
                                        <Typography variant="body1">{selectedForm.event_organisers}</Typography>
                                    </Box>
                                )}

                                {selectedForm.type_of_event && (
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Type of Event</Typography>
                                        <Typography variant="body1">{selectedForm.type_of_event}</Typography>
                                    </Box>
                                )}

                                {selectedForm.event_overview && (
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Event Overview</Typography>
                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {selectedForm.event_overview}
                                        </Typography>
                                    </Box>
                                )}

                                <Divider />
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#009688' }}>
                                    Section 2: Rationale for Participation
                                </Typography>

                                {selectedForm.relevance_to_iod && (
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Relevance to IOD PARC</Typography>
                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {selectedForm.relevance_to_iod}
                                        </Typography>
                                    </Box>
                                )}

                                {selectedForm.expected_benefits && (
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Expected Benefits</Typography>
                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {selectedForm.expected_benefits}
                                        </Typography>
                                    </Box>
                                )}

                                {selectedForm.proposed_outputs && (
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Proposed Outputs</Typography>
                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {selectedForm.proposed_outputs}
                                        </Typography>
                                    </Box>
                                )}

                                {selectedForm.participants && (
                                    <>
                                        <Divider />
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#009688' }}>
                                            Section 3: Participants
                                        </Typography>
                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Name</TableCell>
                                                        <TableCell>Workstream</TableCell>
                                                        <TableCell>Role/Interest</TableCell>
                                                        <TableCell>Funding Source</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {parseParticipants(selectedForm.participants).map((p, idx) => (
                                                        <TableRow key={idx}>
                                                            <TableCell>{p.name || '-'}</TableCell>
                                                            <TableCell>{p.workstream || '-'}</TableCell>
                                                            <TableCell>{p.roleInterest || '-'}</TableCell>
                                                            <TableCell>{p.fundingSource || '-'}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </>
                                )}

                                {selectedForm.costs && (
                                    <>
                                        <Divider />
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#009688' }}>
                                            Section 4: Estimated Costs
                                        </Typography>
                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Category</TableCell>
                                                        <TableCell>Details</TableCell>
                                                        <TableCell align="right">Estimated Cost (£)</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {parseCosts(selectedForm.costs).map((c, idx) => (
                                                        <TableRow key={idx}>
                                                            <TableCell>{c.category}</TableCell>
                                                            <TableCell>{c.details || '-'}</TableCell>
                                                            <TableCell align="right">{c.estimatedCost ? `£${c.estimatedCost}` : '-'}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                    {selectedForm.total_cost && (
                                                        <TableRow sx={{ backgroundColor: '#009688' }}>
                                                            <TableCell colSpan={2} align="right" sx={{ color: '#ffffff', borderBottom: 'none' }}><strong>Total</strong></TableCell>
                                                            <TableCell align="right" sx={{ color: '#ffffff', borderBottom: 'none' }}><strong>£{selectedForm.total_cost.toFixed(2)}</strong></TableCell>
                                                        </TableRow>
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </>
                                )}

                                <Divider />
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#009688' }}>
                                    Section 5: Communications and Follow up
                                </Typography>

                                {selectedForm.planned_communications && (
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Planned Communications</Typography>
                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {selectedForm.planned_communications}
                                        </Typography>
                                    </Box>
                                )}

                                {selectedForm.lead_for_communications && (
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Lead for Communications</Typography>
                                        <Typography variant="body1">{selectedForm.lead_for_communications}</Typography>
                                    </Box>
                                )}

                                {selectedForm.follow_up_actions && (
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Follow-up Actions</Typography>
                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {selectedForm.follow_up_actions}
                                        </Typography>
                                    </Box>
                                )}

                                {selectedForm.rejection_reason && (
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Rejection Reason</Typography>
                                        <Alert severity="warning">{selectedForm.rejection_reason}</Alert>
                                    </Box>
                                )}
                            </Stack>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
                    {selectedForm && selectedForm.status === 'pending' && (
                        <>
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<ApproveIcon />}
                                onClick={() => handleApprove(selectedForm)}
                                disabled={processing}
                            >
                                Approve
                            </Button>
                            <Button
                                variant="contained"
                                color="error"
                                startIcon={<RejectIcon />}
                                onClick={() => {
                                    setDetailsDialogOpen(false);
                                    handleRejectClick(selectedForm);
                                }}
                                disabled={processing}
                            >
                                Reject
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            {/* Rejection Dialog */}
            <Dialog
                open={rejectDialogOpen}
                onClose={() => setRejectDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Reject Event Request</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" gutterBottom>
                        Please provide a reason for rejecting this event request:
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        placeholder="Enter rejection reason..."
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleRejectConfirm}
                        disabled={!rejectionReason.trim() || processing}
                    >
                        Confirm Rejection
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete Event Request</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this event request? This action cannot be undone.
                    </Typography>
                    {selectedForm && (
                        <Typography variant="body2" sx={{ mt: 2, fontWeight: 'bold' }}>
                            {selectedForm.event_title}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDeleteConfirm}
                        disabled={processing}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default EventsAdminPage;
